import { DropDownModel, DropDownWithLabelModel } from "./dropDown";
import Validator, { ValidatedValue } from "./validator";
import { action, makeObservable, observable } from "mobx";

export enum Discounts {
  agentDiscount = "agentDiscount",
  individualRiskAssessment = "individualRiskAssessment",
  managerDiscount = "managerDiscount",
  durManagerDiscount = "durManagerDiscount",
  employeeDiscount = "employeeDiscount",
  specialDiscount = "specialDiscount",
  nnwDiscount = "nnwDiscount",
  otherClaimlessPolicyDiscount = "otherClaimlessPolicyDiscount",
  dedicatedDiscountCode = "dedicatedDiscountCode"
}

export interface SelectedValuesDiscountModel {
  [Discounts.agentDiscount]: ValidatedValue<string>;
  [Discounts.individualRiskAssessment]: ValidatedValue<string>;
  [Discounts.managerDiscount]: ValidatedValue<string>;
  [Discounts.durManagerDiscount]: ValidatedValue<string>;
  [Discounts.employeeDiscount]: ValidatedValue<string>;
  [Discounts.specialDiscount]: ValidatedValue<string>;
  [Discounts.nnwDiscount]: ValidatedValue<string>;
  [Discounts.otherClaimlessPolicyDiscount]: ValidatedValue<OtherClaimlessPolicyDiscount>;
  [Discounts.dedicatedDiscountCode]: ValidatedValue<string>;

  validateAll: () => boolean;
}

export enum OtherClaimlessPolicyDiscountTypes {
  oc = "OC",
  ocAc = "OC_AC",
  safeFarmer = "SAFE_FARMER",
  safeEntrepreneur = "SAFE_ENTREPRENEUR",
  empty = "EMPTY"
}

interface OtherClaimlessPolicyDiscount {
  type: OtherClaimlessPolicyDiscountTypes;
  value: string;
}

export interface DiscountWithMaxValueModel {
  label: string;
  maxValue: number;
}

interface DiscountWithLabelModel {
  label: string;
}

export interface DiscountTypes {
  [Discounts.agentDiscount]: DropDownWithLabelModel;
  [Discounts.individualRiskAssessment]: DiscountWithMaxValueModel;
  [Discounts.managerDiscount]: DiscountWithMaxValueModel;
  [Discounts.durManagerDiscount]: DiscountWithMaxValueModel;
  [Discounts.employeeDiscount]: DropDownWithLabelModel;
  [Discounts.specialDiscount]: DiscountWithMaxValueModel;
  [Discounts.nnwDiscount]: DiscountWithMaxValueModel;
  [Discounts.otherClaimlessPolicyDiscount]: DiscountWithLabelModel;
  [Discounts.dedicatedDiscountCode]: DiscountWithLabelModel;
}

export enum OtherClaimlessPolicyDiscountValues {
  ZERO_PERCENT = "ZERO_PERCENT",
  FIVE_PERCENT = "FIVE_PERCENT",
  TEN_PERCENT = "TEN_PERCENT"
}

export enum DirectDiscountValues {
  FIVE_PERCENT = "FIVE_PERCENT"
}

export function mapOtherClaimlessPolicyDiscountValues(value: OtherClaimlessPolicyDiscountValues | null) {
  switch (value) {
    case OtherClaimlessPolicyDiscountValues.ZERO_PERCENT:
      return "0%";
    case OtherClaimlessPolicyDiscountValues.FIVE_PERCENT:
      return "5%";
    case OtherClaimlessPolicyDiscountValues.TEN_PERCENT:
      return "10%";
    default:
      return "0%";
  }
}

export class Discount {
  private static agentDiscountDropDownValues: DropDownModel[] = [
    { id: "ZERO_PERCENT", displayName: "0%" },
    { id: "ONE_PERCENT", displayName: "1%" },
    { id: "TWO_PERCENT", displayName: "2%" },
    { id: "THREE_PERCENT", displayName: "3%" },
    { id: "FOUR_PERCENT", displayName: "4%" },
    { id: "FIVE_PERCENT", displayName: "5%" }
  ];

  private static employeeDiscountDropDownValues: DropDownModel[] = [
    { id: "ZERO_PERCENT", displayName: "0%" },
    { id: "TEN_PERCENT", displayName: "10%" },
    { id: "TWENTY_PERCENT", displayName: "20%" },
    { id: "THIRTY_PERCENT", displayName: "30%" },
    { id: "FORTY_PERCENT", displayName: "40%" },
    { id: "FIFTY_PERCENT", displayName: "50%" }
  ];

  public static otherClaimlessPolicyDiscountDropDownValues: DropDownModel[] = [
    { id: OtherClaimlessPolicyDiscountTypes.empty, displayName: "" },
    { id: OtherClaimlessPolicyDiscountTypes.oc, displayName: "OC" },
    { id: OtherClaimlessPolicyDiscountTypes.ocAc, displayName: "OC+AC" },
    { id: OtherClaimlessPolicyDiscountTypes.safeFarmer, displayName: "Bezpieczny Rolnik" },
    { id: OtherClaimlessPolicyDiscountTypes.safeEntrepreneur, displayName: "Bezpieczny Przedsiębiorca" }
  ];

  public static discountsConfiguration: DiscountTypes = {
    [Discounts.agentDiscount]: {
      label: "Zniżka agenta",
      dropDown: Discount.agentDiscountDropDownValues
    },
    [Discounts.individualRiskAssessment]: {
      label: "Zwyżka składki za indywidualną ocenę ryzyka",
      maxValue: 1000
    },
    [Discounts.managerDiscount]: {
      label: "Zniżka dodatkowa",
      maxValue: 40
    },
    [Discounts.durManagerDiscount]: {
      label: "Zniżka dodatkowa",
      maxValue: 95
    },
    [Discounts.specialDiscount]: {
      label: "Zniżka za bezszkodowe wznowienie",
      maxValue: 15
    },
    [Discounts.employeeDiscount]: {
      label: "Zniżka pracownicza",
      dropDown: Discount.employeeDiscountDropDownValues
    },
    [Discounts.nnwDiscount]: {
      label: "Zniżka marketingowa NNW",
      maxValue: 95
    },
    [Discounts.otherClaimlessPolicyDiscount]: {
      label: "Zniżka za posiadane innej bezszkodowej polisy w TUZ"
    },
    [Discounts.dedicatedDiscountCode]: {
      label: "Zniżka dedykowana"
    }
  };
}

export interface SelectedValuesDiscountArgs {
  agentDiscount: string;
  individualRiskAssessment: string;
  managerDiscount: string;
  durManagerDiscount: string;
  employeeDiscount: string;
  specialDiscount: string;
  nnwDiscount: string;
  otherClaimlessPolicyDiscount: string;
  dedicatedDiscountCode: string;
}

export class SelectedValuesDiscount implements SelectedValuesDiscountModel {
  @observable agentDiscount: ValidatedValue<string>;
  @observable individualRiskAssessment: ValidatedValue<string>;
  @observable managerDiscount: ValidatedValue<string>;
  @observable durManagerDiscount: ValidatedValue<string>;
  @observable employeeDiscount: ValidatedValue<string>;
  @observable specialDiscount: ValidatedValue<string>;
  @observable nnwDiscount: ValidatedValue<string>;
  @observable otherClaimlessPolicyDiscount: ValidatedValue<OtherClaimlessPolicyDiscount>;
  @observable dedicatedDiscountCode: ValidatedValue<string>;

  constructor(args?: SelectedValuesDiscountArgs) {
    makeObservable(this);

    this.agentDiscount = new ValidatedValue(args?.agentDiscount ?? "ZERO_PERCENT");
    this.individualRiskAssessment = new ValidatedValue((args?.individualRiskAssessment ?? "0"), SelectedValuesDiscount.validator(Discounts.individualRiskAssessment));
    this.managerDiscount = new ValidatedValue((args?.managerDiscount ?? "0"), SelectedValuesDiscount.validator(Discounts.managerDiscount));
    this.durManagerDiscount = new ValidatedValue((args?.durManagerDiscount ?? "0"), SelectedValuesDiscount.validator(Discounts.durManagerDiscount));
    this.employeeDiscount = new ValidatedValue(args?.employeeDiscount ?? "ZERO_PERCENT");
    this.specialDiscount = new ValidatedValue((args?.specialDiscount ?? "0"), SelectedValuesDiscount.validator(Discounts.specialDiscount));
    this.nnwDiscount = new ValidatedValue((args?.nnwDiscount ?? "0"), SelectedValuesDiscount.validator(Discounts.nnwDiscount));
    this.otherClaimlessPolicyDiscount = new ValidatedValue({
      type: args?.otherClaimlessPolicyDiscount as OtherClaimlessPolicyDiscountTypes ?? OtherClaimlessPolicyDiscountTypes.empty,
      value: ""
    });
    this.dedicatedDiscountCode = new ValidatedValue(args?.dedicatedDiscountCode ?? "");
  }

  private static validator(discount: Discounts) {
    const discountConfiguration: any = Discount.discountsConfiguration[discount];
    if (discountConfiguration.hasOwnProperty("maxValue")) {
      // @ts-ignore
      return [{
        name: "maxValue",
        fn: Validator.max(discountConfiguration.maxValue),
        errorMessage: `Maksymalnie ${discountConfiguration.maxValue}%`
      }];
    } else {
      return [];
    }
  }

  @action
  public validateAll = () => {
    this.agentDiscount.validate();
    this.managerDiscount.validate();
    this.durManagerDiscount.validate();
    this.nnwDiscount.validate();
    this.dedicatedDiscountCode.validate();
    this.employeeDiscount.validate();
    this.otherClaimlessPolicyDiscount.validate();
    this.specialDiscount.validate();
    this.individualRiskAssessment.validate();
    const hasError = !!this.agentDiscount.errorMessage
      || !!this.managerDiscount.errorMessage
        || !!this.durManagerDiscount.errorMessage
      || !!this.nnwDiscount.errorMessage
      || !!this.dedicatedDiscountCode.errorMessage
      || !!this.employeeDiscount.errorMessage
      || !!this.otherClaimlessPolicyDiscount.errorMessage
      || !!this.specialDiscount.errorMessage
      || !!this.individualRiskAssessment.errorMessage;

    return hasError;
  }
}
